// ABOUTME: A selectable row — radio ring, title, hint, optional trailing slot.
// ABOUTME: One shape for picking a plan, a saved card or a payment gateway (docs/design/Оплата.dc.html).

import type { FC, ReactNode } from "react"
import { cn } from "@shared/lib/utils"

interface COptionRowProps {
  selected: boolean
  onSelect: () => void
  title: ReactNode
  /** Second line under the title, 13/12px muted. */
  hint?: ReactNode
  /** Pinned to the right — a price, a badge. */
  right?: ReactNode
  disabled?: boolean
  /** Height and padding differ per list: 52px rows by default, 56 for the plan list. */
  className?: string
}

/**
 * The whole row is the control, so it is a `<button>` — not a div with a click handler and a
 * separate `<input type=radio>`. Selection shows twice, in the ring and in the frame, because
 * the tint alone is too quiet on a white card.
 *
 * The tint is `blue-50`; the mock draws `#eff5ff`, one channel away and indistinguishable, and a
 * token beats a hex that would then need pinning of its own.
 */
export const COptionRow: FC<COptionRowProps> = ({
  selected,
  onSelect,
  title,
  hint,
  right,
  disabled = false,
  className,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    onClick={onSelect}
    className={cn(
      "flex min-h-13 w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg border px-3 text-left transition-colors",
      "focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none",
      selected ? "border-primary bg-blue-50" : "border-border bg-white hover:bg-gray-50",
      disabled && "cursor-not-allowed opacity-50",
      className,
    )}
  >
    <span
      aria-hidden
      className={cn(
        "size-4.5 flex-none rounded-full bg-white",
        selected ? "border-[5px] border-primary" : "border border-gray-300",
      )}
    />
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="text-sm font-medium break-words">{title}</span>
      {hint && <span className="text-xs break-words text-muted-foreground">{hint}</span>}
    </span>
    {right && <span className="flex-none">{right}</span>}
  </button>
)
